import type { Metadata } from 'next'

import type { BreadcrumbItem } from '@/app/components/atoms/Breadcrumb'
import type { PerformanceResourceItem } from '@/app/lib/api/types-api'

import { SEO_SITE_ORIGIN } from './constants'
import { buildOpenGraph, buildTwitterCard } from './openGraph'
import { buildPerformanceDescription } from './performanceDescription'
import {
  buildPerformanceBreadcrumb,
  buildPerformanceH1,
  buildPerformanceKeywords,
} from './performanceHeadings'
import { buildPerformanceTitle } from './performanceTitle'

export interface PerformanceSeoPackage {
  readonly title: string
  readonly description: string
  readonly h1: string
  readonly canonical: string
  readonly robots: string
  readonly breadcrumb: readonly BreadcrumbItem[]
  readonly keywords: readonly string[]
  readonly openGraph: NonNullable<Metadata['openGraph']>
  readonly twitter: NonNullable<Metadata['twitter']>
  /** Desviaciones respecto al patrón, para revisión SEO (`06` §8). */
  readonly warnings: readonly string[]
}

/** Defectos del patrón para página nueva de contenido (`06` §5.8). */
const DEFAULT_ROBOTS = 'index,follow'

/**
 * Normaliza la provincia a *sentence case*.
 *
 * El payload real la trae inconsistente entre eventos (`"madrid"` en unos,
 * `"Madrid"` en otros), y `07` exige la misma forma para el mismo concepto en
 * title, H1, breadcrumb y keywords.
 */
export function normalizeProvince(province: string | null | undefined): string | null {
  const trimmed = province?.trim()
  if (!trimmed) return null
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

/**
 * Etiqueta legible de la categoría: `genres[0].name` cuando llega (es el nombre
 * que usa el propio backend), y el slug en sentence case como respaldo.
 */
function resolveCategoryLabel(resource: PerformanceResourceItem, categorySlug: string): string {
  const fromGenres = resource.genres?.[0]?.name?.trim()
  if (fromGenres) return fromGenres

  const words = categorySlug.replace(/-/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : categorySlug
}

/**
 * Paquete SEO de la ficha de evento, generado por **modo patrón**
 * (`docs/seo/06-modo-patron.md`), replicando las filas **Nivel 5** del SEO map.
 * Contrato completo en
 * `specs/043-pagina-de-producto/contracts/seo-pattern-package.md`.
 *
 * **Precedencia (validada con SEO, 2026-08-31): el patrón prevalece sobre los
 * valores SEO que vengan del CMS.** Por eso `seo_title`,
 * `seo_meta_description` y `seo_h1` del recurso **no se consumen**: están
 * declarados en el contrato de datos, llegan vacíos en todo el payload real, y
 * si un gestor los rellenara para un evento concreto seguiría mandando el
 * patrón. Revertirlo sería devolver la precedencia a esos tres campos aquí.
 *
 * Lo que el backend **sí** aporta:
 *   - las variables del patrón (`title`, `venue.province`, categoría),
 *   - `seo_robot` y `seo_canonical` — señales operativas de **indexación** por
 *     evento, no redacción: ignorarlas indexaría fichas marcadas `noindex`,
 *   - `seo_meta_keywords`, que complementa las keywords secundarias.
 *
 * `06` §4 exige comprobar la unicidad del title contra todo el map antes de
 * proponerlo. No es ejecutable en `generateMetadata` — exigiría cargar y
 * recorrer el map en cada render — y la propia norma prevé el caso: se señala y
 * se trata como provisional. La unicidad la aporta de facto el patrón, que
 * incluye nombre del evento y provincia como diferenciador (el mismo criterio
 * geo que §4 prescribe para resolver colisiones).
 */
export function buildPerformanceSeoPackage(
  resource: PerformanceResourceItem,
  categorySlug: string,
  /** Path de la ficha (`/entradas/{categoria}/{evento}/`), para la canónica self. */
  pathname: string,
): PerformanceSeoPackage {
  const name = resource.title?.trim() ?? ''
  const province = normalizeProvince(resource.venue?.province)
  const categoryLabel = resolveCategoryLabel(resource, categorySlug)

  const titleResult = buildPerformanceTitle({ name, province })
  const descriptionResult = buildPerformanceDescription({ name, province })
  const headingsInput = { name, province, categoryLabel, categorySlug }
  const keywords = buildPerformanceKeywords(name, resource.seo_meta_keywords)

  const canonical = resource.seo_canonical?.trim() || `${SEO_SITE_ORIGIN}${pathname}`
  const robots = resource.seo_robot?.trim() || DEFAULT_ROBOTS

  // El póster vertical es la imagen de compartición preferida (es la que el CMS
  // rellena en la práctica); la panorámica solo entra cuando falta.
  const image = resource.image_vertical ?? resource.image_panoramic ?? null

  return {
    title: titleResult.title,
    description: descriptionResult.description,
    h1: buildPerformanceH1(headingsInput),
    canonical,
    robots,
    breadcrumb: buildPerformanceBreadcrumb(headingsInput),
    keywords: keywords.secondary,
    openGraph: buildOpenGraph({
      title: titleResult.title,
      description: descriptionResult.description,
      url: canonical,
      image,
      type: 'website',
    }),
    twitter: buildTwitterCard({
      title: titleResult.title,
      description: descriptionResult.description,
      image,
    }),
    warnings: [
      ...titleResult.warnings,
      ...descriptionResult.warnings,
      // `06` §4: la unicidad no se puede verificar en tiempo de ejecución.
      'Title generado por modo patrón sin verificación de unicidad contra el SEO map (no ejecutable en runtime) — provisional según `06` §4.',
    ],
  }
}

/**
 * NOTA DE PRECEDENCIA (validada con SEO, 2026-08-31)
 * ==================================================
 * `seo_title`, `seo_meta_description` y `seo_h1` del recurso **no se consumen
 * en ningún punto de este módulo**, y es intencionado: el modo patrón prevalece
 * sobre los valores SEO que vengan del CMS. Están declarados en
 * `PerformanceResourceItem` por fidelidad al contrato, y llegan vacíos (`""`)
 * en todo el payload real conocido.
 *
 * `seo_robot` y `seo_canonical` SÍ se consumen: no son redacción sino señales
 * operativas de indexación por evento, y descartarlas indexaría fichas que SEO
 * haya marcado `noindex`.
 *
 * Si se decidiera que un `seo_title` editado a mano prevalezca para su ficha,
 * el cambio es de una línea por campo en `buildPerformanceSeoPackage`.
 */
